/**
 * Page Content Manager - Full Website CMS
 * Allows editing all static page content from a single admin interface.
 *
 * Refactored: PAGE_CONFIG/SECTION_LABELS/getDefaultContentForSection live in
 * ./cms/pageConfig.js. The section editor switch lives in ./cms/SectionEditor.jsx.
 * This file is the orchestrator: auth, page list, load/save, page-level UI.
 */
import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useCMSAuth } from '@/context/CMSAuthContext';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { toast } from 'sonner';
import { AdminLayout } from '@/components/admin/AdminLayout';
import {
  Loader2,
  Save,
  RefreshCw,
  ChevronRight,
  ChevronUp,
  ChevronDown,
  ArrowLeft,
  Edit3,
  Eye,
  SplitSquareHorizontal,
  AlertCircle
} from 'lucide-react';
import { PAGE_CONFIG, SECTION_LABELS } from './cms/pageConfig';
import { SectionEditor } from './cms/SectionEditor';
import { LivePreview } from './cms/LivePreview';

const API_URL = window.location.origin;


export const PageContentManager = () => {
  const { token, logout } = useCMSAuth();
  const [pages, setPages] = useState({});
  const [activePage, setActivePage] = useState('homepage');
  const [activeSection, setActiveSection] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [hasChanges, setHasChanges] = useState({});

  // Live Preview state
  const [viewMode, setViewMode] = useState('split'); // 'edit' | 'split' | 'preview'
  const [previewViewport, setPreviewViewport] = useState('desktop'); // 'desktop' | 'mobile'
  const [previewRefreshKey, setPreviewRefreshKey] = useState(0);

  const handleUnauthorized = (res) => {
    if (res.status === 401) {
      toast.error('Session expired. Please log in again.');
      logout();
      return true;
    }
    return false;
  };

  // Fetch all pages content
  useEffect(() => {
    const fetchAllPages = async () => {
      setLoading(true);
      const fetchedPages = {};
      
      for (const pageSlug of Object.keys(PAGE_CONFIG)) {
        try {
          const res = await fetch(`${API_URL}/api/cms/pages/public/${pageSlug}`);
          if (res.ok) {
            const data = await res.json();
            fetchedPages[pageSlug] = data.content || {};
          }
        } catch (err) {
          console.error(`Failed to fetch ${pageSlug}:`, err);
        }
      }
      
      setPages(fetchedPages);
      setLoading(false);
      
      // Set first section as active
      const firstPage = Object.keys(PAGE_CONFIG)[0];
      const firstSection = PAGE_CONFIG[firstPage]?.sections[0];
      setActivePage(firstPage);
      setActiveSection(firstSection);
    };
    
    fetchAllPages();
  }, []);

  // Update page content
  const updatePageContent = (pageSlug, sectionKey, newContent) => {
    setPages(prev => ({
      ...prev,
      [pageSlug]: {
        ...prev[pageSlug],
        [sectionKey]: newContent
      }
    }));
    setHasChanges(prev => ({
      ...prev,
      [pageSlug]: true
    }));
  };

  // Save page content
  const savePage = async (pageSlug) => {
    if (!token) {
      toast.error('Please login to save changes');
      return;
    }
    
    setSaving(true);
    try {
      const res = await fetch(`${API_URL}/api/cms/pages/${pageSlug}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          content: pages[pageSlug],
          is_active: true
        })
      });
      
      if (handleUnauthorized(res)) {
        setSaving(false);
        return;
      }
      
      if (res.ok) {
        toast.success(`${PAGE_CONFIG[pageSlug]?.label || pageSlug} saved successfully`);
        setHasChanges(prev => ({
          ...prev,
          [pageSlug]: false
        }));
        // Bump preview refresh key → iframe reloads with fresh CMS content
        setPreviewRefreshKey(k => k + 1);
      } else {
        const err = await res.json();
        toast.error(err.detail || 'Failed to save');
      }
    } catch (err) {
      toast.error('Failed to save page content');
      console.error(err);
    }
    setSaving(false);
  };

  // Refresh page from server
  const refreshPage = async (pageSlug) => {
    try {
      const res = await fetch(`${API_URL}/api/cms/pages/public/${pageSlug}`);
      if (res.ok) {
        const data = await res.json();
        setPages(prev => ({
          ...prev,
          [pageSlug]: data.content || {}
        }));
        setHasChanges(prev => ({
          ...prev,
          [pageSlug]: false
        }));
        toast.success('Page refreshed from server');
      }
    } catch (err) {
      toast.error('Failed to refresh page');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-ux-cyan" />
      </div>
    );
  }

  const currentPageConfig = PAGE_CONFIG[activePage];
  const currentPageContent = pages[activePage] || {};
  
  // Guard against undefined currentPageConfig
  if (!currentPageConfig) {
    return (
      <div className="flex items-center justify-center h-64">
        <p className="text-ux-charcoal/60">Page configuration not found</p>
      </div>
    );
  }
  
  const PageIcon = currentPageConfig.icon;

  return (
    <AdminLayout>
    <div className="space-y-6 p-6" data-testid="page-content-manager">
      {/* Header with Back Button */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Link to="/admin/dashboard">
            <Button variant="ghost" size="icon" className="rounded-full">
              <ArrowLeft className="w-5 h-5" />
            </Button>
          </Link>
          <div>
            <h1 className="text-2xl font-bold text-ux-charcoal">Page Content Manager</h1>
            <p className="text-ux-charcoal/60">Edit all website page content from one place</p>
          </div>
        </div>
      </div>

      {/* Page Selection */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
        {Object.entries(PAGE_CONFIG).map(([pageSlug, config]) => {
          const Icon = config.icon;
          const isActive = activePage === pageSlug;
          const changed = hasChanges[pageSlug];
          
          return (
            <button
              key={pageSlug}
              onClick={() => {
                setActivePage(pageSlug);
                setActiveSection(config.sections[0]);
              }}
              className={`relative p-4 rounded-xl border-2 transition-all ${
                isActive
                  ? 'border-ux-cyan bg-ux-cyan/5'
                  : 'border-gray-200 hover:border-gray-300 bg-white'
              }`}
            >
              <div className={`w-10 h-10 rounded-lg bg-gradient-to-br ${config.color} flex items-center justify-center mx-auto mb-2`}>
                <Icon className="w-5 h-5 text-white" />
              </div>
              <p className={`text-sm font-medium ${isActive ? 'text-ux-cyan' : 'text-ux-charcoal'}`}>
                {config.label}
              </p>
              {changed && (
                <span className="absolute top-2 right-2 w-2 h-2 bg-amber-500 rounded-full" />
              )}
            </button>
          );
        })}
      </div>

      {/* View Mode Toggle */}
      <div className="flex items-center justify-between">
        <div className="flex items-center bg-white rounded-lg border p-1" data-testid="view-mode-toggle">
          <button
            onClick={() => setViewMode('edit')}
            className={`flex items-center gap-2 px-3 py-1.5 rounded text-sm font-medium transition-colors ${
              viewMode === 'edit' ? 'bg-ux-cyan text-white' : 'text-ux-charcoal/70 hover:bg-gray-50'
            }`}
            data-testid="view-mode-edit"
          >
            <Edit3 className="w-4 h-4" /> Edit
          </button>
          <button
            onClick={() => setViewMode('split')}
            className={`flex items-center gap-2 px-3 py-1.5 rounded text-sm font-medium transition-colors hidden xl:flex ${
              viewMode === 'split' ? 'bg-ux-cyan text-white' : 'text-ux-charcoal/70 hover:bg-gray-50'
            }`}
            data-testid="view-mode-split"
          >
            <SplitSquareHorizontal className="w-4 h-4" /> Split
          </button>
          <button
            onClick={() => setViewMode('preview')}
            className={`flex items-center gap-2 px-3 py-1.5 rounded text-sm font-medium transition-colors ${
              viewMode === 'preview' ? 'bg-ux-cyan text-white' : 'text-ux-charcoal/70 hover:bg-gray-50'
            }`}
            data-testid="view-mode-preview"
          >
            <Eye className="w-4 h-4" /> Preview
          </button>
        </div>
        <p className="text-xs text-ux-charcoal/40 hidden xl:block">
          Save changes to refresh the live preview
        </p>
      </div>

      {/* Content Editor + Preview */}
      <div className={`grid gap-6 ${
        viewMode === 'split'
          ? 'xl:grid-cols-2 lg:grid-cols-1'
          : 'lg:grid-cols-4'
      }`}>
        {/* EDIT PANE (shown in edit + split modes) */}
        {(viewMode === 'edit' || viewMode === 'split') && (
          <div className={`grid gap-6 ${viewMode === 'split' ? 'grid-cols-1' : 'lg:grid-cols-4 lg:col-span-4'}`}>
            <div className={viewMode === 'split' ? 'space-y-6' : 'lg:grid lg:grid-cols-4 lg:gap-6 lg:col-span-4 contents'}>
              {/* Sections List */}
              <Card className={viewMode === 'split' ? '' : 'lg:col-span-1'}>
                <CardHeader className="pb-3">
                  <CardTitle className="text-lg flex items-center gap-2">
                    <PageIcon className="w-5 h-5" />
                    {currentPageConfig.label}
                  </CardTitle>
                  <CardDescription>Select a section to edit</CardDescription>
                </CardHeader>
                <CardContent className="space-y-1">
                  {currentPageConfig.sections.map((sectionKey) => (
                    <button
                      key={sectionKey}
                      onClick={() => setActiveSection(sectionKey)}
                      className={`w-full flex items-center justify-between px-3 py-2 rounded-lg text-left transition-colors ${
                        activeSection === sectionKey
                          ? 'bg-ux-cyan/10 text-ux-cyan'
                          : 'text-ux-charcoal/70 hover:bg-gray-100'
                      }`}
                    >
                      <span className="text-sm font-medium">
                        {SECTION_LABELS[sectionKey] || sectionKey}
                      </span>
                      <ChevronRight className="w-4 h-4" />
                    </button>
                  ))}
                </CardContent>
              </Card>

              {/* Section Editor */}
              <Card className={viewMode === 'split' ? '' : 'lg:col-span-3'}>
                <CardHeader className="flex-row items-center justify-between space-y-0 pb-4">
                  <div>
                    <CardTitle className="text-lg">
                      {SECTION_LABELS[activeSection] || activeSection}
                    </CardTitle>
                    <CardDescription>
                      Editing {currentPageConfig.label} &gt; {SECTION_LABELS[activeSection] || activeSection}
                    </CardDescription>
                  </div>
                  <div className="flex gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => refreshPage(activePage)}
                    >
                      <RefreshCw className="w-4 h-4 mr-2" />
                      Refresh
                    </Button>
                    <Button
                      size="sm"
                      onClick={() => savePage(activePage)}
                      disabled={saving || !hasChanges[activePage]}
                      className="bg-ux-cyan hover:bg-ux-cyan/90"
                    >
                      {saving ? (
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      ) : (
                        <Save className="w-4 h-4 mr-2" />
                      )}
                      Save Changes
                    </Button>
                  </div>
                </CardHeader>
                <CardContent>
                  {activeSection && (
                    <SectionEditor
                      sectionKey={activeSection}
                      content={currentPageContent[activeSection]}
                      onChange={(newContent) => updatePageContent(activePage, activeSection, newContent)}
                    />
                  )}
                </CardContent>
              </Card>
            </div>
          </div>
        )}

        {/* PREVIEW PANE (shown in preview + split modes) */}
        {(viewMode === 'preview' || viewMode === 'split') && (
          <Card className={viewMode === 'split' ? 'sticky top-4 h-[calc(100vh-180px)]' : 'lg:col-span-4 h-[calc(100vh-280px)]'}>
            <CardContent className="p-0 h-full">
              <LivePreview
                pageConfig={currentPageConfig}
                refreshKey={previewRefreshKey}
                viewport={previewViewport}
                onViewportChange={setPreviewViewport}
              />
            </CardContent>
          </Card>
        )}
      </div>

      {/* Status Bar */}
      {Object.values(hasChanges).some(Boolean) && (
        <div className="fixed bottom-4 left-1/2 -translate-x-1/2 bg-amber-100 border border-amber-300 rounded-lg px-4 py-2 flex items-center gap-3 shadow-lg">
          <AlertCircle className="w-5 h-5 text-amber-600" />
          <span className="text-amber-800 text-sm font-medium">You have unsaved changes</span>
          <Button
            size="sm"
            onClick={() => savePage(activePage)}
            disabled={saving}
            className="bg-amber-600 hover:bg-amber-700"
          >
            Save Now
          </Button>
        </div>
      )}
      </div>
    </AdminLayout>
  );
};

export default PageContentManager;
